import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import authService from '@/services/authService';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({
    email: '',
    password: ''
  });
  const navigate = useNavigate();
  const {
    t
  } = useLanguage();
  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };
  const validatePassword = (password: string) => {
    // This is just a placeholder, replace with your actual password policy
    return password.length >= 8;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setErrors({ email: '', password: '' });

    // Validation
    let formIsValid = true;
    if (!validateEmail(email)) {
      setErrors(prev => ({ ...prev, email: t('invalidEmail') }));
      formIsValid = false;
    }
    if (!validatePassword(password)) {
      setErrors(prev => ({ ...prev, password: t('passwordMinLength') }));
      formIsValid = false;
    }
    if (!formIsValid) {
      setIsLoading(false);
      return;
    }

    try {
      const { user, error, profile } = await authService.signIn(email, password);

      if (error) {
        throw error;
      }

      if (user && profile) {
        // Store user info in localStorage for compatibility
        localStorage.setItem('userEmail', email);
        localStorage.setItem('userId', user.id);
        localStorage.setItem('userRole', profile.role || 'user');
        localStorage.setItem('companyIds', JSON.stringify(profile.company_ids || []));
        
        toast.success(t('loginSuccess'));
        navigate('/company-selection');
      }
    } catch (error: any) {
      console.error('Login error:', error);
      toast.error(error.message || t('loginFailed'));
    } finally {
      setIsLoading(false);
    }
  };
  return <div className="min-h-screen relative overflow-hidden bg-background">
      {/* Language Switcher */}
      <div className="absolute top-4 right-4 z-20">
        <LanguageSwitcher />
      </div>

      {/* Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-background via-purple-900/5 to-background">
        <div className="absolute inset-0 opacity-40" style={{
        backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")"
      }}></div>
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="animate-pulse absolute top-20 left-20 w-72 h-72 bg-purple-500/10 rounded-full blur-3xl"></div>
          <div className="animate-pulse absolute bottom-20 right-20 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animation-delay-2000"></div>
          <div className="animate-pulse absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl animation-delay-4000"></div>
        </div>
      </div>

      {/* Login Form */}
      <div className="relative z-10 flex items-center justify-center min-h-screen p-4">
        <Card className="w-full max-w-md bg-card/80 backdrop-blur-lg border-border shadow-2xl">
          <CardHeader className="text-center space-y-2">
            <CardTitle className="text-3xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
              {t('erpSystem')}
            </CardTitle>
            <CardDescription className="text-muted-foreground">{t('signInDescription')}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-muted-foreground flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  {t('emailAddress')}
                </Label>
                <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} className="bg-input/50 border-input text-foreground placeholder:text-muted-foreground focus:border-purple-400 focus:ring-purple-400/20" placeholder={t('enterEmail')} />
                {errors.email && <p className="text-destructive text-sm mt-1">{errors.email}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-muted-foreground flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  {t('password')}
                </Label>
                <div className="relative">
                  <Input id="password" type={showPassword ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} className="bg-input/50 border-input text-foreground placeholder:text-muted-foreground focus:border-purple-400 focus:ring-purple-400/20 pr-10" placeholder={t('enterPassword')} />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {errors.password && <p className="text-destructive text-sm mt-1">{errors.password}</p>}
                <p className="text-xs text-muted-foreground mt-1">
                  {t('passwordRequirement')}
                </p>
              </div>

              <Button type="submit" disabled={isLoading} className="w-full bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700 text-white font-semibold py-3 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                {isLoading ? <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('signingIn')}
                  </> : t('login')}
              </Button>
            </form>

            <div className="mt-6 space-y-4">
              <div className="text-center">
                <Link to="/reset-password" className="text-sm text-purple-400 hover:text-purple-300 underline">
                  {t('forgotPassword')}
                </Link>
              </div>
              
              <div className="text-center">
                <p className="text-sm text-muted-foreground">
                  {t('noAccount')}{' '}
                  <Link to="/register" className="text-purple-400 hover:text-purple-300 underline">
                    {t('registerNow')}
                  </Link>
                </p>
              </div>

              <div className="text-center">
                <p className="text-xs text-muted-foreground">{t('contactSupport')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>;
};
export default Login;